import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';

interface Resource {
  id: string;
  title: string;
  category: string;
  description: string;
  type: 'template' | 'tutorial' | 'tool' | 'example';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  content: string;
  downloadFileName?: string;
  suggestedKeywords?: string;
}

const resources: Resource[] = [
  {
    id: '1',
    title: '都市爱情剧本模板',
    category: 'templates',
    description: '适合咖啡馆、合租、旧友重逢、职场暧昧这类都市短剧的实用模板。',
    type: 'template',
    difficulty: 'beginner',
    downloadFileName: '都市爱情剧本模板.txt',
    suggestedKeywords: '咖啡馆、旧友重逢、项目合作、暧昧拉扯',
    content: `都市爱情短剧模板

1. 开场钩子
- 在前15秒抛出误会、交易、重逢或身份反差。
- 示例：分手三年的前任，成了我今晚唯一的甲方救星。

2. 人物关系
- 女主：表面冷静，内里有未解决的旧伤。
- 男主：嘴硬但会出手，和女主有旧账或隐秘牵连。
- 施压角色：甲方、家人、旧同事、相亲对象。

3. 三集推进
- 第1集：重逢+试探，必须联手。
- 第2集：误会加深，第三人把旧事翻出来。
- 第3集：真相揭开，关系翻面并留吊点。

4. 常用场景
- 深夜咖啡馆
- 提案会前夜
- 楼梯间/天台
- 散场后的街口

5. 对白方向
- 少说解释，多写试探和反问。
- 每场保留一句能单独截出来传播的金句。`,
  },
  {
    id: '2',
    title: '短视频拍摄入门教程',
    category: 'tutorials',
    description: '把短剧从脚本变成可拍镜头的入门步骤，适合第一次上手的人。',
    type: 'tutorial',
    difficulty: 'beginner',
    content: `短视频拍摄入门教程

一、先定镜头目标
- 每个镜头只做一件事：交代空间、推进情绪、抛出信息、留住吊点。

二、最省事的镜头组合
- 全景：交代地点
- 中景：看人物关系
- 近景：抓情绪和对白
- 特写：钩子物件、表情、转折瞬间

三、手机拍短剧的基本顺序
1. 先拍稳定的大画面
2. 再补人物中近景
3. 最后补特写、手部、门把手、手机屏幕等细节

四、灯光最稳妥的做法
- 白天靠窗拍，人物脸朝向窗户。
- 晚上尽量用一主一辅，不要头顶直照。

五、最容易翻车的地方
- 镜头太满，看不到重点
- 对白写很长，演员很难说顺
- 每个镜头时长差不多，没有节奏变化`,
  },
  {
    id: '3',
    title: '剧本写作工具集',
    category: 'tools',
    description: '把短剧从灵感、写稿、分镜到排期串起来的常用工具和使用建议。',
    type: 'tool',
    difficulty: 'intermediate',
    content: `剧本写作工具集

1. 灵感整理
- 用表格把题材、冲突、反转、金句分开记，后面拼稿更快。

2. 剧本初稿
- 先写一句话概念
- 再写三集推进
- 最后再写第1集完整对白

3. 分镜拆解
- 每场先写人物目标，再决定镜头，不要反过来。

4. 排期协作
- 用列：脚本、分镜、拍摄、剪辑、发布
- 每条视频只保留一个当前阻塞点，别把问题埋在备注里

5. 复盘建议
- 记录开头3秒是什么钩子
- 记录哪句对白被评论区反复引用
- 记录哪一场最容易被跳出`,
  },
  {
    id: '4',
    title: '成功案例分析',
    category: 'examples',
    description: '总结热门短剧常见的钩子、人物关系和反转节奏，直接拿来复盘。',
    type: 'example',
    difficulty: 'intermediate',
    content: `成功案例分析

案例一：时间胶囊
- 爆点：亲情+时间改写+记忆代价
- 有效原因：情感和悬念同时成立，不只靠反转撑着

案例二：咖啡馆的邂逅
- 爆点：旧友重逢+误会被藏了很多年
- 有效原因：场景单一但人物关系有层次，拍摄成本也低

案例三：古风探案
- 爆点：命案只是表层，真正要翻的是旧案
- 有效原因：案件线和人物线捆在一起，不会只剩工具性推理

复盘结论
- 好短剧不是信息越多越好，而是每一集都只推进一个关键决定。
- 真正能留人的，通常是“情绪+信息”双推进。`,
  },
  {
    id: '5',
    title: '古装剧创作指南',
    category: 'templates',
    description: '适合古风探案、婚约反转、朝堂秘事这类题材的写作提纲。',
    type: 'template',
    difficulty: 'advanced',
    downloadFileName: '古装剧创作指南.txt',
    suggestedKeywords: '古风探案、戏班命案、旧案翻出、雨夜追凶',
    content: `古装剧创作指南

一、先定局
- 是查案、婚约、朝堂、复仇，还是多线并行。

二、再定身份差
- 庶女 vs 世子
- 女仵作 vs 县令
- 医官 vs 权臣

三、古装短剧最吃的冲突
- 礼法不允许，但当下必须联手
- 旧案压着现在的人生
- 表面是婚约，底下是利益互保

四、常用场景
- 雨夜祠堂
- 县衙公堂
- 旧王府书房
- 戏台后台

五、对白提醒
- 不要堆古词，重点是语气和分寸
- 让人物说得像人，不要像古装说明书`,
  },
  {
    id: '6',
    title: 'AI配音工具使用教程',
    category: 'tutorials',
    description: '给短剧做配音时，怎么让声音听起来不像模板播报的实操建议。',
    type: 'tutorial',
    difficulty: 'intermediate',
    content: `AI配音工具使用教程

一、先拆人物
- 每个角色先写：年龄感、说话快慢、情绪底色、咬字习惯。

二、配音前先改稿
- 一句话别太长
- 每段只保留一个重音
- 转折处要给停顿位

三、最像机器的原因
- 句子过长
- 标点太少
- 每句情绪一样

四、处理方式
- 强情绪句拆短
- 把停顿和叹词补进去
- 同一角色不同场景保留轻微变化

五、上线前检查
- 金句是否够清楚
- 人物声音是否能区分
- 关键转折有没有被念平`,
  },
];

const ResourceCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeResource, setActiveResource] = useState<Resource | null>(null);

  const categories = useMemo(
    () => [
      { id: 'all', name: '全部资源', count: resources.length },
      { id: 'templates', name: '剧本模板', count: resources.filter((resource) => resource.category === 'templates').length },
      { id: 'tutorials', name: '教程指南', count: resources.filter((resource) => resource.category === 'tutorials').length },
      { id: 'tools', name: '创作工具', count: resources.filter((resource) => resource.category === 'tools').length },
      { id: 'examples', name: '案例分析', count: resources.filter((resource) => resource.category === 'examples').length },
    ],
    [],
  );

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'template': return '📋';
      case 'tutorial': return '📚';
      case 'tool': return '🔧';
      case 'example': return '🎬';
      default: return '📄';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return '入门';
      case 'intermediate': return '中级';
      case 'advanced': return '高级';
      default: return '未知';
    }
  };

  const filteredResources = resources.filter((resource) => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch =
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleDownload = (resource: Resource) => {
    const blob = new Blob([resource.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = resource.downloadFileName || `${resource.title}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleApplyTemplate = (resource: Resource) => {
    const search = resource.suggestedKeywords
      ? `?keywords=${encodeURIComponent(resource.suggestedKeywords)}`
      : '';
    navigate(`/create${search}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">资源中心</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">资源中心</h1>
          <p className="text-gray-600">这里的模板、教程和案例都可以直接查看或下载，不再是假按钮。</p>
        </div>

        <div className="mb-8">
          <div className="relative max-w-md">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索资源..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => (
            <div key={resource.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">{getTypeIcon(resource.type)}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{resource.title}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getDifficultyColor(resource.difficulty)}`}>
                      {getDifficultyText(resource.difficulty)}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{resource.description}</p>

              <div className="space-y-2">
                {resource.type === 'template' ? (
                  <>
                    <button
                      onClick={() => handleDownload(resource)}
                      className="w-full px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      📥 下载模板
                    </button>
                    <button
                      onClick={() => handleApplyTemplate(resource)}
                      className="w-full px-4 py-2 bg-purple-50 text-purple-700 text-sm rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      ✍️ 套用到创作中心
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setActiveResource(resource)}
                    className="w-full px-4 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    📖 查看内容
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">热门推荐</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center text-white text-2xl">
                🔥
              </div>
              <div>
                <h4 className="font-medium text-gray-900">先下模板再去生成</h4>
                <p className="text-sm text-gray-600">先把关键词和结构想清楚，生成质量会稳定很多。</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-lg flex items-center justify-center text-white text-2xl">
                ⭐
              </div>
              <div>
                <h4 className="font-medium text-gray-900">案例要带着问题看</h4>
                <p className="text-sm text-gray-600">重点看钩子、人物关系和每一集的吊点怎么收。</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeResource && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{activeResource.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{activeResource.description}</p>
              </div>
              <button
                onClick={() => setActiveResource(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                关闭
              </button>
            </div>
            <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-7 font-sans">
                {activeResource.content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceCenterPage;
